<div class="flex flex-col gap-2 min-w-80 h-[50vh]">
  @if (createMode()) {
    <p
      class="text-xs font-semibold tracking-wide text-light uppercase px-1 shrink-0 mt-3!"
    >
      Neue Baustelle anlegen
    </p>

    <div class="flex-1 flex flex-col gap-3 overflow-y-auto min-h-0 p-1">
      <div class="flex gap-2">
        <div class="w-24 shrink-0">
          <soft-input
            label="Subnummer"
            [value]="nextSubNumber().toString()"
            [readonly]="true"
            data-testid="construction-site-create-subnummer"
          />
        </div>
        <div class="flex-1 min-w-0">
          <soft-input
            label="Bezeichnung"
            placeholder="z. B. Bauhof Süd"
            [value]="createForm().bezeichnung"
            (valueChange)="updateCreateField('bezeichnung', $event ?? '')"
            [required]="true"
            data-testid="construction-site-create-bezeichnung"
          />
        </div>
      </div>
      <soft-input
        label="Adresse"
        placeholder="Straße, PLZ Ort"
        [value]="createForm().adresse"
        (valueChange)="updateCreateField('adresse', $event ?? '')"
        data-testid="construction-site-create-adresse"
      />

      @if (addressSuggestionsLoading()) {
        <p class="text-xs text-light px-1 shrink-0">Suche Adressen...</p>
      } @else if (addressSuggestionsOpen() && addressSuggestions().length > 0) {
        <div class="flex flex-col gap-1">
          @for (s of addressSuggestions(); track s.label) {
            <button
              type="button"
              class="w-full text-left px-3 py-2 hover:bg-hover focus:bg-hover outline-none text-sm rounded-md border border-default"
              (click)="pickAddressSuggestion(s)"
              data-testid="construction-site-address-suggestion"
            >
              {{ s.label }}
            </button>
          }
        </div>
      }
    </div>
  } @else if (manualMode()) {
    <p
      class="text-xs font-semibold tracking-wide text-light uppercase px-1 shrink-0 mt-3!"
    >
      Manuelle Baustelle
    </p>

    <div class="flex-1 flex flex-col gap-1.5 overflow-y-auto min-h-0 p-1">
      <soft-ds-item
        size="sm"
        variant="default"
        [class.is-selected-row]="isManualSelected()"
        class="min-h-15.5"
      >
        <soft-ds-item-leading>
          <span
            class="inline-flex items-center justify-center size-8 rounded-md shrink-0 bg-primary/10 text-primary"
          >
            <soft-ds-icon name="fa-regular fa-pen" size="md" color="current" />
          </span>
        </soft-ds-item-leading>
        <soft-ds-item-title>
          <soft-input
            class="w-full"
            placeholder="Baustellenbezeichnung eingeben..."
            [value]="manualName()"
            (valueChange)="manualName.set($event ?? '')"
            (keydown.enter)="applyManualConstructionSite()"
            data-testid="construction-site-manual-input"
          />
        </soft-ds-item-title>
        <soft-ds-item-description>
          Wird als freie Bezeichnung übernommen
        </soft-ds-item-description>
        <soft-ds-item-trailing>
          @if (isManualSelected()) {
            <soft-ds-icon name="fa-solid fa-circle-check" size="sm" color="brand" />
          } @else {
            <soft-ds-icon
              name="fa-regular fa-circle"
              size="sm"
              color="current"
              class="opacity-20"
            />
          }
        </soft-ds-item-trailing>
      </soft-ds-item>
    </div>
  } @else {
    <soft-ds-search-input
      class="shrink-0 mt-3 p-1"
      placeholder="Baustelle suchen..."
      [value]="search()"
      (valueChange)="search.set($event ?? '')"
    />

    @if (!loading() && constructionSites().length === 0) {
      <p class="text-sm text-light px-1 shrink-0">Keine Baustellen gefunden.</p>
    } @else if (!loading()) {
      <p class="text-sm text-light px-1 shrink-0">
        {{ constructionSites().length }}
        {{ constructionSites().length === 1 ? 'Baustelle' : 'Baustellen' }} gefunden
      </p>
    } @else {
      <p class="text-sm text-light px-1 shrink-0">Lade Baustellen...</p>
    }

    <div class="flex-1 flex flex-col gap-1.5 overflow-y-auto min-h-0 p-1">
      @if (loading()) {
        @for (_ of skeletonRows; track $index) {
          <soft-ds-item size="sm" variant="outline" class="min-h-15.5">
            <soft-ds-item-leading>
              <soft-ds-loading-skeleton [width]="32" [height]="32" [radius]="6" />
            </soft-ds-item-leading>
            <soft-ds-item-title>
              <soft-ds-loading-skeleton [width]="160" [height]="14" [radius]="4" />
            </soft-ds-item-title>
            <soft-ds-item-description>
              <soft-ds-loading-skeleton [width]="220" [height]="12" [radius]="4" />
            </soft-ds-item-description>
            <soft-ds-item-trailing>
              <soft-ds-loading-skeleton [width]="16" [height]="16" [radius]="999" />
            </soft-ds-item-trailing>
          </soft-ds-item>
        }
      }
      @for (site of filteredConstructionSites(); track rowKey(site); let i = $index) {
        <soft-ds-item
          size="sm"
          [variant]="selectedKey() === rowKey(site) ? 'default' : 'outline'"
          [class.is-selected-row]="selectedKey() === rowKey(site)"
          [interactive]="true"
          (activate)="selectConstructionSite(site)"
        >
          <soft-ds-item-leading>
            @if (isNoConstructionSite(site)) {
              <span
                class="inline-flex items-center justify-center size-8 rounded-md shrink-0"
                [class]="
                  selectedKey() === rowKey(site)
                    ? 'bg-primary text-white'
                    : 'bg-surface-muted text-light'
                "
              >
                <soft-ds-icon name="fa-regular fa-ban" size="md" color="current" />
              </span>
            } @else {
              <soft-site-map-thumb class="sales-map-thumb shrink-0" [index]="i" />
            }
          </soft-ds-item-leading>
          <soft-ds-item-title>
            <span class="inline-flex items-center gap-2">
              <span>{{ rowTitle(site) }}</span>
              <span class="text-xs text-light font-normal">
                {{ rowNumberLabel(site) }}
              </span>
            </span>
          </soft-ds-item-title>

          @if (!isNoConstructionSite(site) && rowAddress(site); as address) {
            <soft-ds-item-description>
              <span class="inline-flex items-center gap-1">
                <soft-ds-icon
                  name="fa-regular fa-location-dot"
                  size="xs"
                  color="current"
                />
                {{ address }}
              </span>
            </soft-ds-item-description>
          }

          <soft-ds-item-trailing>
            <span class="inline-flex items-center gap-1 text-xs text-light">
              <soft-ds-icon name="fa-regular fa-user" size="xs" color="current" />
              {{ rowCustomerName(site) }}
            </span>
            @if (selectedKey() === rowKey(site)) {
              <!--TODO. RADIO BUTTON -->
              <soft-ds-icon name="fa-solid fa-circle-check" size="sm" color="brand" />
            } @else {
              <soft-ds-icon
                name="fa-regular fa-circle"
                size="sm"
                color="current"
                class="opacity-20"
              />
            }
          </soft-ds-item-trailing>
        </soft-ds-item>
      }
    </div>
  }
</div>

<ng-template #bottomTpl>
  <div class="flex items-center justify-between w-full mt-2 gap-2 p-1">
    <div class="flex items-center gap-2">
      <soft-ds-button
        variant="ghost"
        size="md"
        (click)="toggleManualMode()"
        data-testid="construction-site-toggle-manual"
      >
        {{ manualMode() ? 'Suche anzeigen' : 'Manuelle Baustelle' }}
        <ng-template softSlot="leading">
          <soft-ds-icon
            [name]="manualMode() ? 'fa-regular fa-magnifying-glass' : 'fa-regular fa-pen'"
            size="sm"
            color="current"
          />
        </ng-template>
      </soft-ds-button>

      <soft-ds-button
        variant="ghost"
        size="md"
        (click)="toggleCreateMode()"
        data-testid="construction-site-toggle-create"
      >
        {{ createMode() ? 'Suche anzeigen' : 'Neue Baustelle anlegen' }}
        <ng-template softSlot="leading">
          <soft-ds-icon
            [name]="
              createMode() ? 'fa-regular fa-magnifying-glass' : 'fa-regular fa-plus'
            "
            size="sm"
            color="current"
          />
        </ng-template>
      </soft-ds-button>
    </div>

    <div class="flex items-center gap-2">
      @if (isEmbedded()) {
        <soft-ds-button
          variant="secondary"
          size="md"
          (click)="create.emit(local())"
          data-testid="construction-site-step-create"
        >
          {{ '#SALES.WIZARD.CREATE_DOCUMENT' | translate }}
        </soft-ds-button>
      }
      @if (createMode()) {
        <soft-ds-button
          variant="primary"
          size="md"
          [disabled]="!canSubmitCreate() || isCreating()"
          [loading]="isCreating()"
          (click)="createConstructionSite()"
          data-testid="construction-site-create-submit"
        >
          Anlegen
        </soft-ds-button>
      } @else if (isEmbedded()) {
        <soft-ds-button
          variant="primary"
          size="md"
          [disabled]="manualMode() && !manualName().trim()"
          (click)="manualMode() ? applyManualConstructionSite() : confirm()"
          data-testid="construction-site-step-confirm"
        >
          {{ '#SALES.WIZARD.NEXT' | translate }}
          <ng-template softSlot="trailing">
            <soft-ds-icon name="fa-regular fa-arrow-right" size="sm" color="current" />
          </ng-template>
        </soft-ds-button>
      } @else if (manualMode()) {
        <soft-ds-button
          variant="primary"
          size="md"
          [disabled]="!manualName().trim()"
          (click)="applyManualConstructionSite()"
          data-testid="construction-site-manual-apply"
        >
          Übernehmen
        </soft-ds-button>
      }
    </div>
  </div>
</ng-template>
